import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Injector,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  forwardRef,
} from '@angular/core';
import { AbstractControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, map, of } from 'rxjs';
import { AutoLog } from '../../../common/public-api';
import { CurrencyExchangeService } from '../../../core/services/currency-exchange.service';
import { extractDigitsInfo, isNullOrEmpty } from '../../../core/utils';
import { BaseComponent } from '../../../shared/components';
import { CurrencyWidgetComponent } from '../../../shared/components/currency-widget/currency-widget.component';
import { newDecimal } from '../../utils/calculators';

const CURRENCY_WIDGET_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => CurrencyWidgetComponent),
  multi: true,
};
@Component({
  selector: 'sigv-input-currency-widget',
  templateUrl: './input-currency-widget.component.html',
  styleUrls: ['./input-currency-widget.component.scss'],
})
export class InputCurrencyWidgetComponent
  extends BaseComponent
  implements OnInit
{
  /**輸入是否靠右 */
  @Input() isInputRight = false;
  /** formControl物件
   * @description useForm.controls['externalName']
   */
  @Input() bindingFormControl?: AbstractControl | any;
  /**
   * 自動換匯
   */
  @Input() autoExchange = false;

  /**
   * 目標幣別
   */
  @Input() currency?: string;

  /**
   * 是否唯讀
   */
  @Input() readonly = false;

  /**
   * 是否顯示幣別
   */
  @Input() showCurrency = true;

  /**
   * 是否停用
   */
  @Input() disabled = false;

  /**
   * 空值符號
   */
  @Input() replaceNullWith = '-';

  /**
   * 換匯日期
   */
  @Input() exchangeDate?: string;

  /**金額位數設定 */
  @Input() digitConfig?: DigitConfig;

  /**
   * Focus 事件
   */
  @Output() focused = new EventEmitter<any>();

  /**
   * Blur 事件
   */
  @Output() blured = new EventEmitter<any>();

  /**
   * Input 事件
   */
  @Output() inputed = new EventEmitter<any>();

  @Output() exchangeCurrencyChange = new EventEmitter<string>();

  /**
   * 目標幣別設定
   */
  currencySetting?: CurrencySetting;

  /**
   * 目標幣別小數點設定
   */
  digitInfo?: { minInt: any; minFraction: any; maxFraction: any } | any;
  model?: number;
  viewModel?: number;
  _exchangeCurrency?: string;
  inputWidth: string = '';
  currencySignPosition: string = '';
  onModelChange = (event: number | undefined) => {};
  onModelTouched = () => {};

  constructor(
    injector: Injector,
    private currencyExchangeService: CurrencyExchangeService,
    private cd: ChangeDetectorRef
  ) {
    super(injector);
  }

  /**
   * 目標換匯幣別
   */
  @Input() get exchangeCurrency(): string | undefined {
    return this._exchangeCurrency;
  }

  set exchangeCurrency(value: string | undefined) {
    if (isNullOrEmpty(value)) {
      this.viewModel = this.model;
    } else {
      this.doCurrencyExchange(
        this.viewModel,
        this._exchangeCurrency,
        value
      ).subscribe((value: number | undefined) => {
        this.viewModel = value;
      });
    }
    this.updateExchangeCurrency(isNullOrEmpty(value) ? this.currency : value);
    this.setCurrencySetting();
  }

  //#region Angular lifecycle hooks
  @AutoLog()
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currency'] && !changes['currency'].firstChange) {
      if (
        isNullOrEmpty(changes['currency'].previousValue) === false &&
        isNullOrEmpty(changes['currency'].currentValue) === false &&
        this.autoExchange
      ) {
        const previousCurrency = changes['currency'].previousValue as string;
        const currentCurrency = changes['currency'].currentValue as string;
        this.doCurrencyExchange(
          this.model,
          previousCurrency,
          currentCurrency
        ).subscribe((value: number | undefined) => {
          this.viewModel = value;
          this.updateModel(value);
        });
      }
      this.updateExchangeCurrency(this.currency);
      this.setCurrencySetting();
    }
  }

  ngOnInit(): void {
    if (isNullOrEmpty(this._exchangeCurrency)) {
      this.updateExchangeCurrency(this.currency);
    }
    this.setCurrencySetting();
    if (this.showCurrency) {
      this.inputWidth = 'width: 90%';
      this.currencySignPosition = 'margin: 5px 0 0 5px';
    }
  }
  //#endregion Angular lifecycle hooks

  //#region ControlValueAccessor 實作
  @AutoLog()
  writeValue(obj: any): void {
    this.model = obj;
    this.modelToViewModel();
  }

  registerOnChange(fn: any): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onModelTouched = fn;
  }
  //#endregionControlValueAccessor 實作

  isEmpty(value: any): boolean {
    return isNullOrEmpty(value);
  }

  /**
   * 畫面上的顯示值 change 事件
   *
   * @param {number} event
   * @memberof CurrencyWidgetComponent
   */
  @AutoLog()
  onViewModelChange(event: number) {
    this.viewModelToModel();
  }

  /**
   * 從環境設定檔取得目標幣別設定
   *
   * @private
   * @memberof CurrencyWidgetComponent
   */
  @AutoLog()
  private setCurrencySetting(): void {
    const targetCurrency = isNullOrEmpty(this._exchangeCurrency)
      ? this.currency
      : this._exchangeCurrency;
    const currencySettings =
      globalThis.sigv.appContext.envConfig.format.currencies ?? [];
    this.currencySetting = currencySettings.find(
      (x: { currency: string | undefined }) => x.currency === targetCurrency
    );
    if (isNullOrEmpty(this.currencySetting)) {
      throw 'Currency setting is not found.';
    }
    this.digitInfo = extractDigitsInfo(this.currencySetting!.format);
    this.digitInfo['maxInt'] =
      globalThis.sigv.appContext.envConfig.format.numberMaxLength -
      (Number(this.digitInfo.maxFraction) == 0
        ? 2
        : Number(this.digitInfo.maxFraction));
    //設定檔有傳入時則使用設定檔覆蓋
    if (this.digitConfig && !isNullOrEmpty(this.digitConfig.maxFraction)) {
      this.digitInfo.maxFraction = this.digitConfig.maxFraction;
    }
    if (this.digitConfig && !isNullOrEmpty(this.digitConfig.minFraction)) {
      this.digitInfo.minFraction = this.digitConfig.minFraction;
    }
    if (this.digitConfig && !isNullOrEmpty(this.digitConfig.maxInt)) {
      this.digitInfo['maxInt'] = this.digitConfig.maxInt;
    }

    if (this.digitInfo.maxFraction === 0) {
      delete this.digitInfo.maxFraction;
    }
    if (this.digitInfo.minFraction === 0) {
      delete this.digitInfo.minFraction;
    }
  }

  /**
   * 更新 model 值
   *
   * @private
   * @param {number} [value]
   * @memberof CurrencyWidgetComponent
   */
  @AutoLog()
  private updateModel(value?: number): void {
    this.model = value;
    setTimeout(() => this.onModelChange(value), 0);
  }

  /**
   * 更新換匯幣別值
   *
   * @private
   * @param {string} [value]
   * @memberof CurrencyWidgetComponent
   */
  @AutoLog()
  private updateExchangeCurrency(value?: string): void {
    this._exchangeCurrency = value;
    setTimeout(() => {
      this.exchangeCurrencyChange.emit(this.exchangeCurrency);
    }, 0);
  }

  /**
   * 換匯
   *
   * @private
   * @param {number} [amount] 目標金額
   * @param {string} [src] 原始幣別
   * @param {string} [target] 目標幣別
   * @return {*}  {(Observable<number | undefined>)}
   * @memberof CurrencyWidgetComponent
   */
  @AutoLog()
  private doCurrencyExchange(
    amount?: number,
    src?: string,
    target?: string
  ): Observable<number | undefined> {
    if (isNullOrEmpty(amount) || isNullOrEmpty(src) || isNullOrEmpty(target)) {
      return of(amount);
    }
    const observable = this.currencyExchangeService
      .getExchangeRate(src!, target!)
      .pipe(
        map((rate: any) => {
          const value = newDecimal(amount!).mul(rate).toNumber();
          return value;
        })
      );

    return observable;
  }

  /**
   * 畫面上金額換算成實際幣別金額
   *
   * @private
   * @memberof CurrencyWidgetComponent
   */
  @AutoLog()
  private viewModelToModel(): void {
    this.doCurrencyExchange(
      this.viewModel,
      this._exchangeCurrency,
      this.currency
    ).subscribe((value: number | undefined) => {
      this.updateModel(value);
    });
  }

  /**
   * 實際幣別金額換算成畫面上金額
   *
   * @private
   * @memberof CurrencyWidgetComponent
   */
  @AutoLog()
  private modelToViewModel(): void {
    this.doCurrencyExchange(
      this.model,
      this.currency,
      this._exchangeCurrency
    ).subscribe((value: number | undefined) => {
      this.viewModel = value;
    });
  }
}
interface CurrencySetting {
  currency: string;
  format: string;
  step: number;
  round: 'off' | 'up' | 'down';
}
export interface DigitConfig {
  minInt?: number;
  maxInt?: number;
  minFraction?: number;
  maxFraction?: number;
}
